import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, Sparkles, ShieldCheck, TrendingUp, FileCheck2, Building2,
  Landmark, Factory, Store, Cpu, Users, Award, Check, Mail, Globe, Linkedin,
  MessageCircle, Calendar, ArrowUpRight, Zap, LineChart, Lock, Briefcase,
} from "lucide-react";

// --- Content -----------------------------------------------------------------
const CORE_EXPERTISE = [
  "Bank Statutory Audits", "Internal Audits", "PSU & Government Audits",
  "GST Advisory & Compliance", "Income Tax Consultancy", "Risk Management & Internal Controls",
  "Financial Due Diligence", "SOP & Process Documentation",
  "Startup Financial Advisory", "Accounting System Review",
];

const HIGHLIGHTS = [
  { k: "FCA", label: "Fellow Chartered Accountant" },
  { k: "DISA", label: "DISA Qualified" },
  { k: "10+", label: "Years of experience" },
  { k: "Banking", label: "Extensive audit experience" },
  { k: "PSU", label: "Corporate & PSU auditor" },
  { k: "RBI", label: "Guidelines & compliance" },
];

const INDUSTRIES = [
  { icon: Landmark, label: "Banking & Financial Institutions" },
  { icon: Building2, label: "Government Organizations" },
  { icon: ShieldCheck, label: "Public Sector Undertakings" },
  { icon: Factory, label: "Manufacturing" },
  { icon: Store, label: "Trading Businesses" },
  { icon: Briefcase, label: "Service Companies" },
  { icon: Sparkles, label: "Startups" },
  { icon: Users, label: "Professionals" },
];

const SERVICES = [
  {
    icon: ShieldCheck, title: "Audit & Assurance",
    items: ["Bank Statutory Audit", "Internal Audit", "Concurrent Audit", "Stock Audit", "Revenue Audit", "Process Audit"],
  },
  {
    icon: FileCheck2, title: "Taxation",
    items: ["GST Registration & Compliance", "GST Audit Support", "Income Tax Return Filing", "Tax Planning", "TDS Compliance"],
  },
  {
    icon: LineChart, title: "Advisory",
    items: ["Business Process Improvement", "Financial Control Framework", "SOP Development", "Risk Assessment", "Internal Financial Controls (IFC)"],
  },
  {
    icon: Briefcase, title: "Business Support",
    items: ["Company Registration", "LLP Registration", "Accounting Outsourcing", "Virtual CFO Services", "MIS Reporting"],
  },
];

const CHOICE_REASONS = [
  { icon: Zap, t: "Practical, not textbook", b: "Solutions born from ten years inside banks, PSUs, and audit rooms — never theory in a vacuum." },
  { icon: ShieldCheck, t: "RBI-grade rigor", b: "Deep working knowledge of banking regulations, RBI circulars, and the audit trail that keeps you clean." },
  { icon: TrendingUp, t: "Timely & transparent", b: "Fixed timelines, weekly progress, clear invoices. No hidden clauses, no last-minute panic." },
  { icon: Cpu, t: "Technology-first", b: "Every mandate ships with automation: GST workflows, MIS dashboards, and the CA OS platform." },
  { icon: Lock, t: "Confidential by design", b: "Encrypted document exchange, role-based access, and a professional ethics record you can bank on." },
  { icon: Award, t: "Long-term partner", b: "80% of my clients renew year on year. This isn't a transaction — it's a relationship." },
];

// --- Reusable motion helpers -------------------------------------------------
const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { delay: 0.06 * i, duration: 0.55, ease: [0.16, 1, 0.3, 1] },
  }),
};

// --- Sub-components ----------------------------------------------------------
function TopNav() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", on);
    return () => window.removeEventListener("scroll", on);
  }, []);
  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all ${scrolled ? "bg-white/85 backdrop-blur-xl border-b border-border" : "bg-transparent"
        }`}
      data-testid="landing-nav"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <a href="#top" className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-primary text-white grid place-items-center font-heading font-bold text-sm">CA</div>
          <div className="leading-tight">
            <div className="font-heading font-semibold text-sm tracking-tight">Noor Alam</div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">FCA · Founder</div>
          </div>
        </a>
        <nav className="hidden md:flex items-center gap-8 text-sm">
          <a href="#services" className="text-muted-foreground hover:text-foreground transition-colors">Services</a>
          <a href="#expertise" className="text-muted-foreground hover:text-foreground transition-colors">Expertise</a>
          <a href="#technology" className="text-muted-foreground hover:text-foreground transition-colors">Technology</a>
          <a href="#contact" className="text-muted-foreground hover:text-foreground transition-colors">Contact</a>
        </nav>
        <div className="flex items-center gap-2">
          <Link
            to="/login"
            className="hidden sm:inline-flex text-xs font-medium text-muted-foreground hover:text-foreground px-3 py-2 rounded-md transition-colors"
            data-testid="nav-signin"
          >
            Sign in to CA OS
          </Link>
          <a
            href="#contact"
            className="inline-flex items-center gap-1.5 text-xs font-medium bg-foreground text-background hover:opacity-90 px-3.5 py-2 rounded-md transition-opacity"
            data-testid="nav-book"
          >
            <Calendar size={12} /> Book consultation
          </a>
        </div>
      </div>
    </header>
  );
}

function Hero() {
  return (
    <section id="top" className="relative pt-32 pb-24 lg:pt-40 lg:pb-32 overflow-hidden">
      {/* Ambient gradient orbs — behind content but not behind section bg */}
      <div className="absolute inset-0 pointer-events-none z-0">
        <div className="absolute -top-24 -left-32 w-[520px] h-[520px] rounded-full bg-primary/10 blur-3xl" />
        <div className="absolute top-40 -right-20 w-[420px] h-[420px] rounded-full bg-brand-violet/10 blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-[1.15fr_1fr] gap-14 lg:gap-20 items-center relative z-10">
        <div>
          <motion.div
            initial="hidden" animate="show" variants={fadeUp} custom={0}
            className="inline-flex items-center gap-2 rounded-full border border-border bg-white/60 backdrop-blur px-3 py-1 text-[11px] font-medium mb-6"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
            Trusted by Banks · PSUs · Corporates · MSMEs
          </motion.div>

          <motion.h1
            initial="hidden" animate="show" variants={fadeUp} custom={1}
            className="font-heading text-5xl sm:text-6xl lg:text-[72px] leading-[1.02] tracking-[-0.03em] font-semibold"
          >
            Turning{" "}
            <span className="relative inline-block">
              <span className="relative z-10">Compliance</span>
              <span className="absolute inset-x-0 bottom-1 h-3 bg-primary/20 -z-0" />
            </span>{" "}
            into <span className="italic font-light text-primary">Business Confidence</span>.
          </motion.h1>

          <motion.p
            initial="hidden" animate="show" variants={fadeUp} custom={2}
            className="mt-7 text-lg text-muted-foreground max-w-xl leading-relaxed"
          >
            A decade of statutory audits, forensic reviews, GST work, and
            technology-driven finance for banks, PSUs, corporates, and the
            businesses building India&rsquo;s next chapter.
          </motion.p>

          <motion.div
            initial="hidden" animate="show" variants={fadeUp} custom={3}
            className="mt-9 flex flex-wrap gap-3"
          >
            <a
              href="#contact"
              className="group inline-flex items-center gap-2 bg-foreground text-background px-5 py-3 rounded-lg text-sm font-medium hover:opacity-90 transition-opacity"
              data-testid="hero-book"
            >
              Book a consultation
              <ArrowRight size={15} className="transition-transform group-hover:translate-x-0.5" />
            </a>
            <a
              href="#services"
              className="inline-flex items-center gap-2 border border-border bg-white/70 backdrop-blur px-5 py-3 rounded-lg text-sm font-medium hover:bg-white transition-colors"
              data-testid="hero-services"
            >
              View services
            </a>
          </motion.div>

          <motion.div
            initial="hidden" animate="show" variants={fadeUp} custom={4}
            className="mt-14 grid grid-cols-3 gap-8 max-w-md"
          >
            {[
              { k: "10+", l: "years" },
              { k: "FCA", l: "Fellow · DISA" },
              { k: "80%", l: "client renewal" },
            ].map((s) => (
              <div key={s.k}>
                <div className="font-heading text-3xl font-semibold tracking-tight">{s.k}</div>
                <div className="text-xs text-muted-foreground mt-1 uppercase tracking-widest">{s.l}</div>
              </div>
            ))}
          </motion.div>
        </div>

        {/* Portrait / card */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.3, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="relative"
        >
          <div className="relative aspect-[4/5] max-w-md ml-auto">
            {/* Layered cards for depth */}
            <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-primary/90 to-brand-violet/90 rotate-2 shadow-2xl" />
            <div className="absolute inset-0 rounded-2xl bg-card border border-border -rotate-1 shadow-xl" />
            <div className="absolute inset-0 rounded-2xl bg-white border border-border p-8 flex flex-col justify-between shadow-lg">
              <div>
                <div className="flex items-center gap-2 mb-6">
                  <div className="w-10 h-10 rounded-full bg-primary/10 grid place-items-center">
                    <ShieldCheck size={18} className="text-primary" />
                  </div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Professional Profile</div>
                </div>
                <h3 className="font-heading text-2xl font-semibold tracking-tight leading-tight">CA Noor Alam, FCA</h3>
                <p className="text-sm text-muted-foreground mt-1">Chartered Accountant · Founder</p>
                <div className="mt-6 space-y-2.5">
                  {[
                    "Bank & PSU Statutory Auditor",
                    "Internal Audit Specialist",
                    "DISA · RBI Compliance Expert",
                    "Virtual CFO for MSMEs",
                  ].map((t) => (
                    <div key={t} className="flex items-center gap-2 text-sm">
                      <Check size={14} className="text-primary shrink-0" />
                      {t}
                    </div>
                  ))}
                </div>
              </div>
              <div className="pt-6 border-t border-border flex items-center justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Currently building</div>
                  <div className="text-sm font-medium mt-0.5">CA OS Platform</div>
                </div>
                <Link
                  to="/login"
                  className="w-9 h-9 rounded-full bg-foreground text-background grid place-items-center hover:opacity-90 transition-opacity"
                  data-testid="hero-portrait-app-link"
                  aria-label="Open CA OS"
                >
                  <ArrowUpRight size={15} />
                </Link>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function Expertise() {
  return (
    <section id="expertise" className="py-24 lg:py-32 border-t border-border">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-[0.9fr_1.1fr] gap-14 lg:gap-24">
        <div>
          <div className="text-xs uppercase tracking-widest text-primary font-semibold mb-4">Why work with me</div>
          <h2 className="font-heading text-4xl sm:text-5xl font-semibold tracking-[-0.02em] leading-tight">
            You don&rsquo;t just hire a CA.
            <br />
            <span className="text-muted-foreground">You get a builder.</span>
          </h2>
          <p className="mt-6 text-muted-foreground leading-relaxed">
            A professional with practical experience in statutory audits,
            internal audits, forensic reviews, GST compliance, financial
            reporting, process improvement, and technology-driven finance —
            all in one seat.
          </p>

          <div className="mt-10 grid grid-cols-2 gap-3">
            {HIGHLIGHTS.map((h, i) => (
              <motion.div
                key={h.k}
                initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.3 }}
                variants={fadeUp} custom={i}
                className="border border-border bg-white rounded-lg p-4"
              >
                <div className="font-heading font-semibold text-primary text-lg tracking-tight">{h.k}</div>
                <div className="text-xs text-muted-foreground mt-1">{h.label}</div>
              </motion.div>
            ))}
          </div>
        </div>

        <div>
          <div className="text-xs uppercase tracking-widest text-primary font-semibold mb-4">Core Expertise</div>
          <h3 className="font-heading text-2xl font-semibold tracking-tight mb-8">Every specialty a growing firm needs.</h3>
          <div className="space-y-1">
            {CORE_EXPERTISE.map((e, i) => (
              <motion.div
                key={e}
                initial={{ opacity: 0, x: -12 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4, delay: i * 0.04 }}
                viewport={{ once: true, amount: 0.4 }}
                className="group flex items-center justify-between py-4 border-b border-border last:border-0 hover:bg-muted/30 -mx-3 px-3 rounded-md transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className="font-mono text-xs text-muted-foreground w-6">{String(i + 1).padStart(2, "0")}</span>
                  <span className="text-sm font-medium">{e}</span>
                </div>
                <ArrowUpRight size={14} className="text-muted-foreground group-hover:text-primary opacity-0 group-hover:opacity-100 transition-opacity" />
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function Industries() {
  return (
    <section className="py-20 border-t border-border bg-gradient-to-b from-transparent to-primary/[0.02]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="text-xs uppercase tracking-widest text-primary font-semibold mb-3 text-center">Industries served</div>
        <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-center max-w-2xl mx-auto">
          From RBI-regulated banks to first-time founders.
        </h2>

        <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-3">
          {INDUSTRIES.map((it, i) => (
            <motion.div
              key={it.label}
              initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }}
              variants={fadeUp} custom={i}
              className="border border-border bg-white rounded-xl p-5 hover:shadow-md hover:-translate-y-0.5 transition-all"
            >
              <div className="w-10 h-10 rounded-lg bg-primary/10 grid place-items-center mb-4">
                <it.icon size={18} className="text-primary" />
              </div>
              <div className="text-sm font-medium leading-snug">{it.label}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Technology() {
  return (
    <section
      id="technology"
      className="py-24 lg:py-32 relative overflow-hidden bg-[#0A1424]"
      style={{
        backgroundImage:
          "radial-gradient(circle at 20% 20%, rgba(37,99,235,0.28), transparent 50%), radial-gradient(circle at 80% 80%, rgba(139,92,246,0.18), transparent 50%)",
      }}
    >
      {/* Subtle grid overlay */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-25 pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)",
          backgroundSize: "60px 60px",
        }}
      />

      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-[1fr_0.9fr] gap-14 lg:gap-20 items-center text-white relative">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 backdrop-blur px-3 py-1 text-[11px] font-medium mb-6">
            <Sparkles size={11} className="text-brand-gold" />
            Technology meets accounting
          </div>
          <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-[-0.02em] leading-[1.05]">
            Finance is evolving fast.
            <br />
            <span className="text-white/60">So am I.</span>
          </h2>
          <p className="mt-7 text-white/70 leading-relaxed text-lg max-w-xl">
            Alongside professional practice, I&rsquo;m building CA OS — a
            practice management platform for chartered accountants. Automation
            for GST, MIS, payment tracking, and compliance. So firms spend less
            time in Excel and more time growing.
          </p>

          <div className="mt-10 space-y-3">
            {[
              { i: Zap, t: "Automated recurring tasks", b: "GSTR-3B, TDS, ITR — spawn tasks on schedule, invoice on completion." },
              { i: LineChart, t: "Firm-wide MIS in real time", b: "Every partner, every client, every deadline — one dashboard." },
              { i: ShieldCheck, t: "Audit trail on everything", b: "Every mutation logged. Every soft-delete recoverable. Regulator-ready." },
            ].map((f) => (
              <div key={f.t} className="flex gap-4 items-start">
                <div className="w-9 h-9 rounded-lg bg-white/10 grid place-items-center shrink-0 mt-0.5">
                  <f.i size={15} className="text-brand-gold" />
                </div>
                <div>
                  <div className="text-sm font-semibold">{f.t}</div>
                  <div className="text-sm text-white/60 mt-0.5">{f.b}</div>
                </div>
              </div>
            ))}
          </div>

          <Link
            to="/login"
            className="mt-10 inline-flex items-center gap-2 bg-white text-[#0A1424] px-5 py-3 rounded-lg text-sm font-semibold hover:bg-white/90 transition-colors group"
            data-testid="tech-cta-open-app"
          >
            Open CA OS
            <ArrowRight size={15} className="transition-transform group-hover:translate-x-0.5" />
          </Link>
        </div>

        {/* Mock product panel */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          viewport={{ once: true, amount: 0.3 }}
          className="relative"
        >
          <div className="rounded-xl border border-white/10 bg-[#0F172A]/80 backdrop-blur-xl overflow-hidden shadow-2xl">
            <div className="flex items-center gap-2 px-4 py-2.5 border-b border-white/10">
              <span className="w-2.5 h-2.5 rounded-full bg-white/20" />
              <span className="w-2.5 h-2.5 rounded-full bg-white/20" />
              <span className="w-2.5 h-2.5 rounded-full bg-white/20" />
              <span className="text-[10px] text-white/40 ml-2 font-mono">caos.app / dashboard</span>
            </div>
            <div className="p-5">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-white/40 font-semibold">This week</div>
                  <div className="font-heading text-xl font-semibold mt-1">Good morning, Noor</div>
                </div>
                <div className="text-xs bg-brand-gold/20 text-brand-gold px-2 py-1 rounded-md font-mono">3 due today</div>
              </div>
              <div className="grid grid-cols-3 gap-2 mb-5">
                {[
                  { l: "Open tasks", v: "47" },
                  { l: "Overdue", v: "3", warn: true },
                  { l: "Due this wk", v: "12" },
                ].map((k) => (
                  <div key={k.l} className="border border-white/10 rounded-lg p-3">
                    <div className={`font-heading text-2xl font-semibold ${k.warn ? "text-red-400" : ""}`}>{k.v}</div>
                    <div className="text-[10px] uppercase tracking-widest text-white/40 mt-1">{k.l}</div>
                  </div>
                ))}
              </div>
              <div className="space-y-2">
                {[
                  { t: "GSTR-3B · Acme Trading Co", s: "Due tomorrow", tone: "amber" },
                  { t: "Statutory Audit · Kolhapur Bank", s: "In progress", tone: "blue" },
                  { t: "TDS Return · Q3 · 4 clients", s: "Assigned to Rhea", tone: "neutral" },
                ].map((r) => (
                  <div key={r.t} className="flex items-center gap-3 p-2.5 rounded-md border border-white/5 bg-white/[0.02]">
                    <span className={`w-1.5 h-1.5 rounded-full ${r.tone === "amber" ? "bg-brand-gold" : r.tone === "blue" ? "bg-primary" : "bg-white/30"
                      }`} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm truncate">{r.t}</div>
                      <div className="text-[11px] text-white/40">{r.s}</div>
                    </div>
                    <ArrowRight size={12} className="text-white/30" />
                  </div>
                ))}
              </div>
            </div>
          </div>
          {/* Floating accents */}
          <div className="absolute -bottom-4 -right-4 w-32 h-32 rounded-2xl bg-brand-gold/20 blur-2xl -z-10" />
          <div className="absolute -top-6 -left-6 w-40 h-40 rounded-full bg-primary/30 blur-3xl -z-10" />
        </motion.div>
      </div>
    </section>
  );
}

function Services() {
  return (
    <section id="services" className="py-24 lg:py-32 border-t border-border">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex items-end justify-between gap-6 flex-wrap mb-14">
          <div>
            <div className="text-xs uppercase tracking-widest text-primary font-semibold mb-3">Services</div>
            <h2 className="font-heading text-4xl sm:text-5xl font-semibold tracking-[-0.02em] leading-tight max-w-xl">
              One partner. Every mandate a growing firm needs.
            </h2>
          </div>
          <a
            href="#contact"
            className="text-sm font-medium text-primary hover:underline inline-flex items-center gap-1"
          >
            Discuss your requirement <ArrowUpRight size={13} />
          </a>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {SERVICES.map((s, i) => (
            <motion.div
              key={s.title}
              initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }}
              variants={fadeUp} custom={i}
              className="group relative bg-white border border-border rounded-2xl p-6 hover:border-primary/40 hover:shadow-lg transition-all"
              data-testid={`service-card-${s.title.replace(/\W+/g, "-").toLowerCase()}`}
            >
              <div className="w-11 h-11 rounded-lg bg-primary/10 text-primary grid place-items-center mb-6 group-hover:bg-primary group-hover:text-white transition-colors">
                <s.icon size={18} />
              </div>
              <h3 className="font-heading font-semibold text-lg tracking-tight">{s.title}</h3>
              <ul className="mt-4 space-y-2">
                {s.items.map((it) => (
                  <li key={it} className="text-sm text-muted-foreground flex items-start gap-2">
                    <Check size={13} className="text-primary shrink-0 mt-0.5" />
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function WhyClients() {
  return (
    <section className="py-24 lg:py-32 border-t border-border bg-[#F5F7FF]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-3xl">
          <div className="text-xs uppercase tracking-widest text-primary font-semibold mb-3">Why clients choose me</div>
          <h2 className="font-heading text-4xl sm:text-5xl font-semibold tracking-[-0.02em] leading-tight">
            Practical. Punctual. Precise.
          </h2>
        </div>

        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {CHOICE_REASONS.map((r, i) => (
            <motion.div
              key={r.t}
              initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }}
              variants={fadeUp} custom={i}
              className="bg-white border border-border rounded-2xl p-6"
            >
              <r.icon size={20} className="text-primary mb-4" />
              <h3 className="font-heading font-semibold text-base tracking-tight">{r.t}</h3>
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{r.b}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CTA() {
  return (
    <section id="contact" className="py-24 lg:py-32 border-t border-border">
      <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
        <div className="text-xs uppercase tracking-widest text-primary font-semibold mb-4">Let&rsquo;s talk</div>
        <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-[-0.02em] leading-[1.05]">
          Let&rsquo;s build stronger businesses <em className="font-light text-primary">together</em>.
        </h2>
        <p className="mt-6 text-lg text-muted-foreground max-w-2xl mx-auto">
          Whether you&rsquo;re a startup, MSME, corporate, bank, or government
          organization — schedule a consultation and let&rsquo;s discuss your
          requirements.
        </p>

        <div className="mt-10 flex flex-wrap justify-center gap-3">
          <a
            href="mailto:admin@canooralam.com"
            className="inline-flex items-center gap-2 bg-foreground text-background px-6 py-3.5 rounded-lg text-sm font-semibold hover:opacity-90 transition-opacity"
            data-testid="cta-email"
          >
            <Mail size={14} /> admin@canooralam.com
          </a>
          <Link
            to="/login"
            className="inline-flex items-center gap-2 border border-border bg-white px-6 py-3.5 rounded-lg text-sm font-semibold hover:bg-muted/50 transition-colors"
            data-testid="cta-open-app"
          >
            <Sparkles size={14} /> Open CA OS
          </Link>
        </div>

        <div className="mt-14 grid sm:grid-cols-3 gap-3 max-w-2xl mx-auto">
          {[
            { icon: Mail, label: "Email", value: "admin@canooralam.com", href: "mailto:admin@canooralam.com" },
            { icon: Globe, label: "Website", value: "canooralam.com", href: "https://www.canooralam.com" },
            { icon: Linkedin, label: "LinkedIn", value: "in/canooralam", href: "https://linkedin.com" },
          ].map((c) => (
            <a
              key={c.label}
              href={c.href}
              target={c.href.startsWith("http") ? "_blank" : undefined}
              rel="noreferrer"
              className="border border-border bg-white rounded-lg p-4 hover:border-primary/40 hover:shadow-sm transition-all text-left"
            >
              <c.icon size={14} className="text-primary mb-2" />
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{c.label}</div>
              <div className="text-sm font-medium truncate mt-0.5">{c.value}</div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="border-t border-border py-12 text-sm">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid md:grid-cols-[1.4fr_1fr_1fr] gap-10">
        <div>
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg bg-primary text-white grid place-items-center font-heading font-bold text-sm">CA</div>
            <div className="font-heading font-semibold tracking-tight">Noor Alam, FCA</div>
          </div>
          <p className="text-muted-foreground leading-relaxed max-w-md text-xs">
            Professional Chartered Accountant providing audit, taxation,
            advisory, and technology-enabled financial solutions across India.
          </p>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-4">Product</div>
          <ul className="space-y-2 text-xs">
            <li><Link to="/login" className="hover:text-primary transition-colors">Sign in to CA OS</Link></li>
            <li><a href="#technology" className="hover:text-primary transition-colors">Platform</a></li>
            <li><a href="#services" className="hover:text-primary transition-colors">Services</a></li>
          </ul>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-4">Connect</div>
          <ul className="space-y-2 text-xs">
            <li><a href="mailto:admin@canooralam.com" className="hover:text-primary transition-colors inline-flex items-center gap-1.5"><Mail size={11} /> admin@canooralam.com</a></li>
            <li><a href="https://wa.me/" className="hover:text-primary transition-colors inline-flex items-center gap-1.5" target="_blank" rel="noreferrer"><MessageCircle size={11} /> WhatsApp</a></li>
            <li><a href="https://linkedin.com" className="hover:text-primary transition-colors inline-flex items-center gap-1.5" target="_blank" rel="noreferrer"><Linkedin size={11} /> LinkedIn</a></li>
          </ul>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-6 lg:px-10 mt-10 pt-6 border-t border-border flex flex-wrap items-center justify-between gap-3 text-[11px] text-muted-foreground">
        <div>© 2026 CA Noor Alam. All rights reserved.</div>
        <div>Made with care for Indian finance professionals.</div>
      </div>
    </footer>
  );
}

// --- Page --------------------------------------------------------------------
export default function LandingPage() {
  return (
    <div className="bg-[#FAFBFF] text-foreground" data-testid="landing-page">
      <TopNav />
      <Hero />
      <Expertise />
      <Industries />
      <Technology />
      <Services />
      <WhyClients />
      <CTA />
      <Footer />
    </div>
  );
}
